// @givmo/board — a committee's members: add one, change a membership, end it (CBG-YR6-REG,
// `board-committees.md` §5). No frame drew these dialogs; they take c2's member row for their facts.
//
//   * Anyone serving on the board, director or not, may be added (an officer who isn't a director
//     serves on committees). The seats offered are the roster's serving seats not already on it.
//   * Someone who isn't on the board (CBG-YR6-OUTSIDE, frame o1) is the list's last choice: a name and
//     an email of record, and one act writes their seat, scoped to the committees, with this
//     membership (`new_seat`). Someone already seated that way is offered for a further committee as
//     "{name} (not on the board)". Never from Board members: a seat with no committee opens nothing.
//   * "Votes on the committee" is the organization's record: yes unless they say otherwise, and never
//     set from whether the person is a director (the roster's fact, shown beside it, not read as it).
//   * One chair: naming a second is refused by the backend in its own words, shown as served.
//   * Changing sends only what changed (`null` clears a date), so a save that changes nothing records
//     nothing. Ending keeps the membership in the committee's history, with its last day if given.
//
// Every membership write is the Owner's act (`board_member:manage`) and a person's own (agent-never
// item 8): no AI makes it.
//
// CBG-UX9 (frame c1; `roster/invite-step.tsx`): adding someone who isn't on the board makes a new seat,
// so a holder of `board_member:grant_access` is then asked, in this same dialog, whether to invite
// them to the committee. The add answers with the committee: the new seat is the one membership whose
// seat wasn't on it before, at the address just typed. Someone already seated is never asked: their
// seat is not new, and has its own invitation or has joined.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeMemberOut,
  type BoardCommitteeMemberRole,
  type BoardCommitteeOut,
  type BoardCommitteeRecordedIn,
} from '@givmo/api-client';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { extractBoardMembers } from '../roster/roster-decode';
import { InviteStep, asKept, useInviteStep } from '../roster/invite-step';
import { activeMembers } from './committees-decode';
import { committeeOnlyWords as co } from './committee-only-words';
import { RecordedInPicker } from './recorded-in-picker';
import { committeesWords as w } from './committees-words';

/** The "Who" choice for someone who isn't on the board (CBG-YR6-OUTSIDE): never a seat id. */
const SOMEONE_NOT_ON_THE_BOARD = 'someone-not-on-the-board';

/** An end before its start, said here as the backend says it (and refused there too). */
export function membershipDatesProblem(from: string, to: string): string | null {
  return from.length > 0 && to.length > 0 && to < from ? w.datesOutOfOrder : null;
}

/** A change's body: only the fields that differ from the stored membership. */
export function membershipChanges(
  member: BoardCommitteeMemberOut,
  next: {
    role: BoardCommitteeMemberRole;
    votes: boolean;
    from: string;
    to: string;
    recordedIn: BoardCommitteeRecordedIn | null;
  },
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  if (next.role !== member.role) changes.role = next.role;
  if (next.votes !== member.votes) changes.votes = next.votes;
  const fromOrNull = next.from.length > 0 ? next.from : null;
  const toOrNull = next.to.length > 0 ? next.to : null;
  if (fromOrNull !== (member.serves_from ?? null)) changes.serves_from = fromOrNull;
  if (toOrNull !== (member.serves_to ?? null)) changes.serves_to = toOrNull;
  // A source this reader can't open is shown as none; it is replaced only when the person picks one.
  const hidden =
    member.source_kind !== 'entered' && (member.source === null || member.source === undefined);
  const storedSource =
    member.source_kind === 'entered' ||
    hidden ||
    member.source === null ||
    member.source === undefined
      ? null
      : `${member.source_kind}:${member.source.id}`;
  const nextSource =
    next.recordedIn === null ? null : `${next.recordedIn.kind}:${next.recordedIn.id}`;
  if (nextSource !== storedSource && !(hidden && nextSource === null)) {
    changes.recorded_in = next.recordedIn;
  }
  return changes;
}

/** The seat an add made: the one membership in its answer whose seat wasn't on the committee before. */
export function addedSeatId(before: BoardCommitteeOut, after: BoardCommitteeOut): string | null {
  const known = new Set(before.members.map((row) => row.board_member_id));
  const added = after.members.filter((row) => !known.has(row.board_member_id));
  return added.length === 1 ? (added[0]?.board_member_id ?? null) : null;
}

export function MemberDialog({
  orgRef,
  committee,
  member,
  onClose,
  onDone,
  canInvite = false,
  onAdded,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  /** null to add a member; a membership to change or end it. */
  member: BoardCommitteeMemberOut | null;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
  /** CBG-UX9: the person holds `board_member:grant_access`, so a new seat is offered its invitation. */
  canInvite?: boolean;
  /** CBG-UX9: the committee as the add left it, while the dialog stays to ask about the invitation. */
  onAdded?: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // The new seat's invitation and address change: the roster's read says where the seat is.
  const step = useInviteStep(orgRef, () => {
    void queryClient.invalidateQueries({
      queryKey: operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }).queryKey,
    });
  });
  const changing = member !== null;
  const [seatId, setSeatId] = useState<string>('');
  // Someone who isn't on the board: the two facts their seat needs.
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const newSeat = !changing && seatId === SOMEONE_NOT_ON_THE_BOARD;
  const [role, setRole] = useState<BoardCommitteeMemberRole>(member?.role ?? 'member');
  const [votes, setVotes] = useState(member?.votes ?? true);
  const [from, setFrom] = useState(member?.serves_from ?? '');
  const [to, setTo] = useState(member?.serves_to ?? '');
  const [recordedIn, setRecordedIn] = useState<BoardCommitteeRecordedIn | null>(
    member !== null && member.source_kind !== 'entered' && member.source
      ? { kind: member.source_kind, id: member.source.id }
      : null,
  );
  const [ending, setEnding] = useState(false);
  const [lastDay, setLastDay] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Adding only: the board's serving seats not already on this committee.
  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    enabled: !changing,
    select: extractBoardMembers,
  });
  const onIt = useMemo(
    () => new Set(activeMembers(committee).map((row) => row.board_member_id)),
    [committee],
  );
  const seatOptions = useMemo(
    () =>
      (seatsQuery.data ?? [])
        .filter((seat) => seat.status === 'active' && !onIt.has(seat.id))
        .sort((left, right) => left.display_name.localeCompare(right.display_name))
        .map((seat) => ({
          value: seat.id,
          label:
            seat.scope === 'committees'
              ? co.seatNotOnTheBoard(seat.display_name)
              : seat.display_name,
        })),
    [seatsQuery.data, onIt],
  );
  const whoOptions = useMemo(
    () => [...seatOptions, { value: SOMEONE_NOT_ON_THE_BOARD, label: co.someoneNotOnTheBoard }],
    [seatOptions],
  );

  const save = useMutation({
    mutationFn: async (): Promise<BoardCommitteeOut> => {
      if (member !== null) {
        const { data } = await operations.officerUpdateBoardCommitteeMember({
          path: { org_ref: orgRef, committee_id: committee.id, membership_id: member.id },
          body: membershipChanges(member, { role, votes, from, to, recordedIn }),
          throwOnError: true,
        });
        return data;
      }
      const { data } = await operations.officerAddBoardCommitteeMember({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: {
          ...(newSeat
            ? {
                new_seat: { display_name: newName.trim(), email_of_record: newEmail.trim() },
              }
            : { board_member_id: seatId }),
          role,
          votes,
          ...(from.length > 0 ? { serves_from: from } : {}),
          ...(to.length > 0 ? { serves_to: to } : {}),
          ...(recordedIn !== null ? { recorded_in: recordedIn } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      const added = newSeat
        ? newName.trim()
        : (seatsQuery.data?.find((seat) => seat.id === seatId)?.display_name ?? '');
      toast.success(changing ? w.memberChanged : w.memberAdded(added));
      // CBG-UX9 (frame c1): a new seat, and someone who may invite it.
      const invitee = newSeat && canInvite ? addedSeatId(committee, updated) : null;
      if (invitee !== null) {
        onAdded?.(updated);
        step.offer({ id: invitee, display_name: added, email_of_record: asKept(newEmail) }, [
          committee.name,
        ]);
        return;
      }
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.memberFailed),
  });

  const end = useMutation({
    mutationFn: async (): Promise<BoardCommitteeOut> => {
      if (member === null) throw new Error('nothing to end');
      const { data } = await operations.officerEndBoardCommitteeMember({
        path: { org_ref: orgRef, committee_id: committee.id, membership_id: member.id },
        body: {
          ...(lastDay.length > 0 ? { serves_to: lastDay } : {}),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.ended);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.endFailed),
  });

  const pending = save.isPending || end.isPending;
  const problem = membershipDatesProblem(from, to);
  const unchanged =
    member !== null &&
    Object.keys(membershipChanges(member, { role, votes, from, to, recordedIn })).length === 0;
  const heading = ending
    ? w.endTitle(member?.display_name ?? '')
    : changing
      ? w.changeMemberTitle(member?.display_name ?? '')
      : w.addMemberTitle(committee.name);

  // CBG-UX9 (frame c1): the add has saved; the same dialog asks whether to invite them.
  if (step.seat !== null) {
    return (
      <Dialog.Root open onOpenChange={(next) => !next && !step.busy && onClose()}>
        <Dialog.Content
          aria-label={step.title}
          className="flex flex-col gap-16 overflow-y-auto"
          testId="committee-member-add-dialog"
        >
          <InviteStep step={step} testIdPrefix="committee-member" onClose={onClose} />
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex flex-col gap-16 overflow-y-auto"
        testId={changing ? 'committee-member-change-dialog' : 'committee-member-add-dialog'}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>

        {ending ? (
          <>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {w.endBody}
            </p>
            <TextField
              label={w.endLastDayLabel}
              type="date"
              value={lastDay}
              disabled={pending}
              onChange={(event) => setLastDay(event.target.value)}
              testId="committee-member-end-day"
            />
            <Textarea
              label={w.noteLabel}
              value={note}
              maxLength={500}
              rows={2}
              disabled={pending}
              onChange={(event) => setNote(event.target.value)}
              testId="committee-member-end-note"
            />
          </>
        ) : (
          <>
            {!changing ? (
              <div className="flex flex-col gap-6">
                <Select
                  label={w.seatLabel}
                  appearance="saas"
                  value={seatId}
                  disabled={pending || seatsQuery.isPending}
                  onValueChange={(next) => setSeatId(next ?? '')}
                  options={whoOptions}
                  testId="committee-member-seat"
                />
                {seatsQuery.isSuccess && seatOptions.length === 0 && !newSeat ? (
                  <FieldNote>{w.noSeats}</FieldNote>
                ) : null}
              </div>
            ) : null}

            {/* CBG-YR6-OUTSIDE (frame o1): someone who isn't on the board, as a group dependent on
                "Who", marked by a leading rule (never a fill, never indent alone): the frame's rule
                painted the text's own colour, which `text-strong` names. Removed before finishing:
                the email field's own note; the line beneath says what matters about this seat. */}
            {newSeat ? (
              <div
                className="flex flex-col gap-12 border-l-2 border-text-strong pl-16"
                data-testid="committee-member-new-seat"
              >
                <TextField
                  label={co.nameLabel}
                  value={newName}
                  disabled={pending}
                  onChange={(event) => setNewName(event.target.value)}
                  testId="committee-member-new-seat-name"
                />
                <TextField
                  label={co.emailLabel}
                  type="email"
                  value={newEmail}
                  disabled={pending}
                  onChange={(event) => setNewEmail(event.target.value)}
                  testId="committee-member-new-seat-email"
                />
                <FieldNote testId="committee-member-new-seat-scope">
                  {co.scopeNote(committee.name)}
                </FieldNote>
              </div>
            ) : null}

            <Select
              label={w.roleLabel}
              appearance="saas"
              value={role}
              disabled={pending}
              onValueChange={(next) => setRole(next === 'chair' ? 'chair' : 'member')}
              options={[
                { value: 'member', label: w.roleMember },
                { value: 'chair', label: w.roleChair },
              ]}
              testId="committee-member-role"
            />

            <div className="flex flex-col gap-6">
              <label className="inline-flex cursor-pointer items-center gap-10">
                <Checkbox
                  appearance="saas"
                  checked={votes}
                  disabled={pending}
                  onCheckedChange={(next) => setVotes(next === true)}
                  testId="committee-member-votes"
                />
                <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong">
                  {w.votesLabel}
                </span>
              </label>
              <FieldNote>{w.votesNote}</FieldNote>
            </div>

            <div className="grid grid-cols-2 gap-12 max-sm:grid-cols-1">
              <TextField
                label={w.servesFromLabel}
                type="date"
                value={from}
                disabled={pending}
                onChange={(event) => setFrom(event.target.value)}
                testId="committee-member-from"
              />
              <TextField
                label={w.servesToLabel}
                type="date"
                value={to}
                disabled={pending}
                onChange={(event) => setTo(event.target.value)}
                testId="committee-member-to"
              />
            </div>
            {/* CBG-FIX85: when the minutes count this membership from (C8 as built, §7.1). */}
            <FieldNote testId="committee-member-from-note">
              {changing ? w.servesFromNoteChange : w.servesFromNoteAdd}
            </FieldNote>
            {problem !== null ? <FieldNote>{problem}</FieldNote> : null}

            <RecordedInPicker
              orgRef={orgRef}
              value={recordedIn}
              onChange={setRecordedIn}
              disabled={pending}
              testId="committee-member-recorded-in"
            />
          </>
        )}

        {error !== null ? (
          <ErrorMessage testId="committee-member-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-10">
          {changing && !ending ? (
            <Button
              appearance="saas"
              tone="ghost"
              className="mr-auto"
              disabled={pending}
              onClick={() => {
                setError(null);
                setEnding(true);
              }}
              testId="committee-member-end"
            >
              {w.endMembership}
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={ending ? () => setEnding(false) : onClose}
            testId="committee-member-cancel"
          >
            {w.cancel}
          </Button>
          {ending ? (
            <Button
              appearance="saas"
              tone="danger"
              loading={end.isPending}
              disabled={pending}
              onClick={() => {
                setError(null);
                end.mutate();
              }}
              testId="committee-member-end-confirm"
            >
              {w.endSave}
            </Button>
          ) : (
            <Button
              appearance="saas"
              tone="primary"
              loading={save.isPending}
              disabled={
                pending ||
                problem !== null ||
                unchanged ||
                (!changing && seatId.length === 0) ||
                (newSeat && (newName.trim().length === 0 || newEmail.trim().length === 0))
              }
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              testId="committee-member-save"
            >
              {changing ? w.memberChangeSave : w.memberSave}
            </Button>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
